import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import PublicHeader from "../components/PublicHeader";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [paymentInfo, setPaymentInfo] = useState(null);

  const token = localStorage.getItem("token");
  let isClient = false;
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      isClient = payload.role === "client";
    } catch (_) {}
  }

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }

    let attempts = 0;
    const maxAttempts = 5;

    const poll = async () => {
      try {
        const res = await fetch(`${BACKEND}/api/payments/check/${sessionId}`);
        if (!res.ok) throw new Error("Erreur");
        const data = await res.json();
        if (data.payment_status === "paid") {
          setPaymentInfo(data);
          setStatus("success");
          return;
        } else if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        attempts++;
        if (attempts < maxAttempts) setTimeout(poll, 2000);
        else setStatus("pending");
      } catch {
        setStatus("error");
      }
    };
    poll();
  }, [sessionId]);

  const dashboardLink = isClient ? "/portal" : "/dashboard";
  const retryLink = isClient ? "/portal/boutique" : "/pos";

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/30">
      <PublicHeader />
      <div className="flex items-center justify-center p-4 pt-16">
        <div className="w-full max-w-md">
          <Card className="border-border/50 shadow-xl" data-testid="payment-result-card">
            {status === "loading" && (
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  <Loader2 className="h-12 w-12 text-primary animate-spin" />
                </div>
                <CardTitle>Vérification du paiement</CardTitle>
                <CardDescription>Veuillez patienter...</CardDescription>
              </CardHeader>
            )}

            {status === "success" && (
              <>
                <CardHeader className="text-center">
                  <div className="flex justify-center mb-4">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                      <CheckCircle className="h-8 w-8 text-green-600" />
                    </div>
                  </div>
                  <CardTitle data-testid="payment-success-title">Paiement réussi !</CardTitle>
                  <CardDescription>
                    Votre paiement de {paymentInfo?.amount_total ? (paymentInfo.amount_total / 100).toFixed(2) : "—"} EUR a été confirmé.
                  </CardDescription>
                </CardHeader>
                <CardContent className="text-center text-sm text-muted-foreground">
                  <p>Vos articles sont à récupérer en boutique :</p>
                  <p className="font-medium text-foreground mt-1">Super U de Bergevin, 97110 Pointe-à-Pitre</p>
                  <p className="mt-1">Lun-Ven : 8h-14h (sauf Mer) | Sam : 9h-13h</p>
                </CardContent>
                <CardFooter className="flex justify-center gap-3">
                  <Link to={dashboardLink}>
                    <Button data-testid="back-to-dashboard-btn">
                      {token ? (isClient ? "Mon espace" : "Tableau de bord") : "Se connecter"}
                    </Button>
                  </Link>
                  <Link to="/boutique">
                    <Button variant="outline">Continuer mes achats</Button>
                  </Link>
                </CardFooter>
              </>
            )}

            {status === "pending" && (
              <>
                <CardHeader className="text-center">
                  <CardTitle>Paiement en cours</CardTitle>
                  <CardDescription>
                    Votre paiement est en cours de traitement. Vous recevrez un email de confirmation.
                  </CardDescription>
                </CardHeader>
                <CardFooter className="flex justify-center">
                  <Link to={token ? dashboardLink : "/login"}>
                    <Button>{token ? "Mon espace" : "Se connecter"}</Button>
                  </Link>
                </CardFooter>
              </>
            )}

            {(status === "error" || status === "expired") && (
              <>
                <CardHeader className="text-center">
                  <div className="flex justify-center mb-4">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                      <XCircle className="h-8 w-8 text-red-600" />
                    </div>
                  </div>
                  <CardTitle>Erreur de paiement</CardTitle>
                  <CardDescription>
                    {status === "expired"
                      ? "La session de paiement a expiré."
                      : "Une erreur est survenue lors de la vérification du paiement."}
                  </CardDescription>
                </CardHeader>
                <CardFooter className="flex justify-center gap-3">
                  <Link to="/boutique">
                    <Button>Retour à la boutique</Button>
                  </Link>
                </CardFooter>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
